import useProductsReturnRates from "@babackapp/private-api-client/client/use-products-return-rates";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@babackapp/ui/components/card";
import {
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAnalyticsFilters } from "../../hooks/use-analytics-filters";
import { useDebouncedLoading } from "../../hooks/use-debounced-loading";
import { GroupByToggle } from "./group-by-toggle";
import { getProductsTableColumns, ProductRow } from "./products-table-columns";

const ROW_HEIGHT = 52;

// Grid template for consistent column alignment
// Fixed widths sized for longest translations (German)
const getGridTemplate = (hasExpand: boolean) =>
  hasExpand
    ? "48px 1fr 200px 140px 130px 170px"
    : "1fr 200px 140px 130px 170px";

export function ProductsAnalyticsTable() {
  const t = useTranslation();
  const {
    shop,
    dateRange,
    filters,
    groupBy,
    sort,
    productFilter,
    includeAllOrders,
    setAvailableCountries,
    setProductFilter,
    setIsDataLoading,
    setSort,
  } = useAnalyticsFilters();

  // Convert context sort to TanStack table sorting state
  const sorting: SortingState = useMemo(
    () => [
      { id: sort.column, desc: sort.direction === "desc" },
      { id: "title", desc: false },
    ],
    [sort.column, sort.direction],
  );

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      if (newSorting.length > 0) {
        const { id, desc } = newSorting[0];
        if (
          id === "ordersCount" ||
          id === "returnsCount" ||
          id === "returnRate"
        ) {
          setSort(id, desc ? "desc" : "asc");
        }
      }
    },
    [sorting, setSort],
  );
  const expandedStorageKey = `analytics-expanded-${shop.id}`;
  const [expanded, setExpanded] = useState<ExpandedState>(() => {
    try {
      const stored = sessionStorage.getItem(expandedStorageKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(expandedStorageKey, JSON.stringify(expanded));
    } catch {
      /* ignore */
    }
  }, [expanded, expandedStorageKey]);
  const parentRef = useRef<HTMLDivElement>(null);

  const resolution = filters.get("resolution")?.value.join(",");
  const reason = filters.get("reason")?.value.join(",");
  const country = filters.get("country")?.value.join(",");

  const {
    data: responseData,
    isLoading,
    isFetching,
  } = useProductsReturnRates(shop.id, {
    fromTimestamp: dateRange.from,
    toTimestamp: dateRange.to,
    ...(resolution && { resolution }),
    ...(reason && { reason }),
    ...(country && { country }),
    ...(productFilter?.productId && { productId: productFilter.productId }),
    ...(productFilter?.catalogId && { catalogId: productFilter.catalogId }),
    ...(includeAllOrders && { includeAllOrders: true }),
  });

  // Update available countries when data is fetched
  useEffect(() => {
    if (responseData?.countries) {
      setAvailableCountries(responseData.countries);
    }
  }, [responseData?.countries, setAvailableCountries]);

  const showLoader = useDebouncedLoading(isFetching);

  // Sync loading state to context for map overlay
  useEffect(() => {
    setIsDataLoading(showLoader);
  }, [showLoader, setIsDataLoading]);

  const productsData = responseData?.products;

  const tableData: ProductRow[] = useMemo(() => {
    if (!productsData) return [];

    // Group by SKU (using variant SKUs)
    if (groupBy === "sku") {
      const skuMap = new Map<
        string,
        {
          sku: string;
          title: string;
          imageUrl?: string;
          ordersCount: number;
          returnsCount: number;
        }
      >();

      productsData.forEach((product) => {
        // If product has variants, use variant SKUs
        if (product.variants && product.variants.length > 0) {
          product.variants.forEach((variant) => {
            const sku =
              variant.sku || `${product.productId}-${variant.variantId}`;
            const existing = skuMap.get(sku);

            // Use variant title if it contains product title
            const variantTitle = variant.title;
            const title = variantTitle
              .toLowerCase()
              .includes(product.title.toLowerCase())
              ? variantTitle
              : `${product.title} - ${variantTitle}`;

            if (existing) {
              existing.ordersCount += variant.ordersCount;
              existing.returnsCount += variant.returnsCount;
            } else {
              skuMap.set(sku, {
                sku,
                title,
                imageUrl: product.imageUrl ?? undefined,
                ordersCount: variant.ordersCount,
                returnsCount: variant.returnsCount,
              });
            }
          });
        } else {
          // No variants, use product SKU
          const sku = product.sku || product.productId;
          const existing = skuMap.get(sku);

          if (existing) {
            existing.ordersCount += product.ordersCount;
            existing.returnsCount += product.returnsCount;
          } else {
            skuMap.set(sku, {
              sku,
              title: product.title,
              imageUrl: product.imageUrl ?? undefined,
              ordersCount: product.ordersCount,
              returnsCount: product.returnsCount,
            });
          }
        }
      });

      return Array.from(skuMap.values()).map((item) => ({
        id: item.sku,
        catalogId: item.sku, // For SKU grouping, use SKU as catalogId
        title: item.title,
        image: item.imageUrl,
        sku: item.sku,
        ordersCount: item.ordersCount,
        returnsCount: item.returnsCount,
        returnRate:
          item.ordersCount > 0
            ? (item.returnsCount / item.ordersCount) * 100
            : 0,
      }));
    }

    // Default: group by productId with variants as sub-rows
    // If only 1 variant, don't show sub-row - show variant name in parentheses
    // Exception: when a variant filter (catalogId) is active, always show sub-rows
    const isVariantFilter = !!productFilter?.catalogId;
    return productsData.map((product) => {
      const catalogId = product.babackCatalogId ?? product.productId;
      const variants = product.variants ?? [];
      const showSubRows =
        variants.length > 1 || (isVariantFilter && variants.length > 0);
      const singleVariant =
        !showSubRows && variants.length === 1 ? variants[0] : null;

      // Translate orphan products marker from backend
      const isOrphan = product.title === "__orphans__";
      let title = isOrphan ? t("analytics.orphanProducts") : product.title;
      if (singleVariant && !isOrphan) {
        const variantTitle = singleVariant.title;
        if (variantTitle.toLowerCase().includes(product.title.toLowerCase())) {
          title = variantTitle;
        } else {
          title = `${product.title} (${variantTitle})`;
        }
      }

      return {
        id: product.productId,
        catalogId,
        title,
        image: product.imageUrl ?? undefined,
        sku: product.sku ?? undefined,
        ordersCount: product.ordersCount,
        returnsCount: product.returnsCount,
        returnRate: product.returnRate,
        isOrphan,
        variants: showSubRows
          ? variants.map((variant) => ({
              id: `${product.productId}-${variant.variantId}`,
              catalogId: variant.babackCatalogId ?? catalogId,
              productId: product.productId,
              variantId: variant.variantId,
              title: variant.title,
              sku: variant.sku ?? undefined,
              ordersCount: variant.ordersCount,
              returnsCount: variant.returnsCount,
              returnRate: variant.returnRate,
            }))
          : undefined,
      };
    });
  }, [productsData, groupBy, productFilter?.catalogId]);

  const showExpandColumn = groupBy === "productId";
  const handleFilterProduct = useCallback(
    (filter: { productId?: string; catalogId?: string; title: string }) => {
      setProductFilter(filter);
    },
    [setProductFilter],
  );

  const handleClearProductFilter = useCallback(() => {
    setProductFilter(null);
  }, [setProductFilter]);

  const columns = useMemo(
    () =>
      getProductsTableColumns(t, {
        showExpandColumn,
        onFilterProduct: handleFilterProduct,
        onClearProductFilter: handleClearProductFilter,
        activeProductFilter: productFilter,
      }),
    [
      t,
      showExpandColumn,
      handleFilterProduct,
      handleClearProductFilter,
      productFilter,
    ],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSubRows: (row) => row.variants as ProductRow[] | undefined,
    onSortingChange: handleSortingChange,
    onExpandedChange: setExpanded,
    state: {
      sorting,
      expanded,
    },
  });

  const { rows } = table.getRowModel();

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const fromDate = format(dateRange.from, "d MMM yyyy");
  const toDate = format(dateRange.to, "d MMM yyyy");

  const gridTemplate = getGridTemplate(showExpandColumn);

  return (
    <Card className="col-span-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-md">
              {t("analytics.productsAnalysis")}
            </CardTitle>
            <CardDescription>
              {fromDate} - {toDate}
            </CardDescription>
          </div>
          <GroupByToggle />
        </div>
      </CardHeader>
      <CardContent className="relative">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-pulse text-muted-foreground">
              {t("misc.loading")}
            </div>
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            {/* Header */}
            <div
              className="grid bg-muted/50 border-b"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {table.getHeaderGroups().map((headerGroup) =>
                headerGroup.headers.map((header) => (
                  <div
                    key={header.id}
                    className="px-3 py-1.5 text-left text-sm font-medium text-muted-foreground flex items-center"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </div>
                )),
              )}
            </div>

            {/* Virtualized body */}
            <div
              ref={parentRef}
              className="overflow-auto"
              style={{ height: Math.min(rows.length * ROW_HEIGHT, 500) }}
            >
              {rows.length === 0 ? (
                <div className="flex items-center justify-center h-24 text-muted-foreground">
                  {t("misc.noResultFound")}
                </div>
              ) : (
                <div
                  style={{
                    height: virtualizer.getTotalSize(),
                    position: "relative",
                  }}
                >
                  {virtualItems.map((virtualRow) => {
                    const row = rows[virtualRow.index];
                    const isSubRow = row.depth > 0;

                    return (
                      <div
                        key={row.id}
                        className={`grid border-b group ${
                          isSubRow ? "bg-muted/30" : "hover:bg-muted/50"
                        }`}
                        style={{
                          gridTemplateColumns: gridTemplate,
                          height: ROW_HEIGHT,
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <div
                            key={cell.id}
                            className="px-3 flex items-center text-sm overflow-hidden"
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        {showLoader && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-[1px] rounded-md">
            <div className="animate-pulse text-muted-foreground text-sm">
              {t("misc.loading")}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
